import type { FC, ReactNode } from 'react';
import { Check, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { SectionLabel } from '@/components/ui/section-label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import type { ReferencePreview } from '@/features/toolView/utils/transformToolsData';
import { plural } from '../lib/wearFormat';

export type WearSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface WearContextPanelProps {
  references: ReferencePreview[];
  toolsLoading: boolean;
  toolsError: boolean;
  onToolsRetry: () => void;
  toolId: string | null;
  onToolChange: (toolId: string) => void;
  onReset: () => void;
  /** Step 1's diameter block. */
  diameterField: ReactNode;
  /** Step 2 (shown once a tool is picked). */
  nextOperation: ReactNode;
  canSave: boolean;
  saveStatus: WearSaveStatus;
  onSave: () => void;
  needsDiameter: boolean;
}

/** Left panel of the tool-wear tab: tool, cutting diameter, next operation, save. */
export const WearContextPanel: FC<WearContextPanelProps> = ({
  references,
  toolsLoading,
  toolsError,
  onToolsRetry,
  toolId,
  onToolChange,
  onReset,
  diameterField,
  nextOperation,
  canSave,
  saveStatus,
  onSave,
  needsDiameter,
}) => {
  const { t, language } = useTranslation();
  const selected = references
    .flatMap((ref) => ref.tools.map((tool) => ({ ...tool, reference: ref.reference })))
    .find((tool) => tool.id === toolId);

  return (
    <Card className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <SectionLabel>{t('mlRul.context.badge')}</SectionLabel>
        {toolId !== null && (
          <Button variant="ghost" size="sm" onClick={onReset} data-testid="wear-reset">
            {t('mlRul.context.reset')}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-2xs font-semibold text-primary-foreground">
              1
            </span>
            <span className="text-sm font-medium text-foreground">{t('mlWear.context.tool')}</span>
          </div>
          <Select value={toolId ?? ''} onValueChange={onToolChange} disabled={toolsLoading}>
            <SelectTrigger className="w-full" data-testid="wear-tool-trigger">
              <SelectValue placeholder={t('mlWear.context.toolPlaceholder')}>
                {selected && (
                  <span className="flex items-center gap-1.5">
                    <span>{selected.id}</span>
                    <span className="text-xs text-muted-foreground">· {selected.reference}</span>
                  </span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {references.map((ref) => (
                <SelectGroup key={ref.reference}>
                  <SelectLabel>{ref.reference}</SelectLabel>
                  {ref.tools.map((tool) => (
                    <SelectItem key={tool.id} value={tool.id}>
                      {tool.id}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          {toolsError && (
            <Callout size="inline" tone="error">
              {t('mlWear.context.toolsLoadError')}{' '}
              <Button variant="link" size="inline" onClick={onToolsRetry}>
                {t('mlWear.context.retry')}
              </Button>
            </Callout>
          )}
          {selected && (
            <p className="text-xs text-muted-foreground" data-testid="wear-tool-help">
              {selected.traceCount > 0
                ? plural(t, 'mlWear.context.toolTraces', selected.traceCount, {
                    date: selected.lastTraceDate
                      ? new Date(selected.lastTraceDate).toLocaleDateString(language)
                      : '—',
                  })
                : t('mlWear.context.toolNoTrace')}
            </p>
          )}
          {toolId !== null && diameterField}
        </div>

        {toolId !== null && nextOperation}
      </div>

      <Button
        onClick={onSave}
        disabled={!canSave || saveStatus === 'saved'}
        loading={saveStatus === 'saving'}
        data-testid="wear-save"
        className="w-full"
      >
        {saveStatus === 'saved' ? (
          <Check aria-hidden />
        ) : (
          saveStatus !== 'saving' && <Save aria-hidden />
        )}
        {t(
          saveStatus === 'saved'
            ? 'mlWear.context.saved'
            : saveStatus === 'saving'
              ? 'mlWear.context.saving'
              : 'mlWear.context.save'
        )}
      </Button>
      {needsDiameter && toolId !== null && (
        <p
          className="-mt-3 text-center text-xs text-muted-foreground"
          data-testid="wear-needs-diameter"
        >
          {t('mlWear.context.needsDiameter')}
        </p>
      )}
      {saveStatus === 'error' && (
        <Callout
          size="inline"
          tone="error"
          className="-mt-3 justify-center"
          data-testid="wear-save-error"
        >
          {t('mlWear.context.saveError')}
        </Callout>
      )}
    </Card>
  );
};
